import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Star, Loader2, HelpCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

type VersionStatus = 'development' | 'beta' | 'stable' | 'deprecated';

interface ProviderVersion {
  id: string;
  provider_id: string;
  version: string;
  status: VersionStatus;
  config: Record<string, unknown>;
  endpoints: Record<string, unknown>;
  changelog: string | null;
  is_available: boolean;
  is_default: boolean;
  released_at: string | null;
  created_at: string;
}

interface ProviderVersionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  providerName: string;
}

const statusConfig: Record<VersionStatus, { label: string; color: string }> = {
  development: { label: 'Desenvolvimento', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  beta: { label: 'Beta', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  stable: { label: 'Estável', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  deprecated: { label: 'Descontinuado', color: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const emptyVersion = {
  version: '',
  status: 'development' as VersionStatus,
  config: '{}',
  endpoints: '{}',
  changelog: '',
  is_available: false,
  is_default: false,
};

export function ProviderVersionsDialog({
  open,
  onOpenChange,
  providerId,
  providerName,
}: ProviderVersionsDialogProps) {
  const [versions, setVersions] = useState<ProviderVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ProviderVersion | null>(null);
  const [formData, setFormData] = useState(emptyVersion);
  const [saving, setSaving] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && providerId) {
      fetchVersions();
    }
  }, [open, providerId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('provider_versions')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVersions((data as ProviderVersion[]) || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast({
        title: 'Erro ao carregar versões',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingVersion(null);
    setFormData(emptyVersion);
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (version: ProviderVersion) => {
    setEditingVersion(version);
    setFormData({
      version: version.version,
      status: version.status,
      config: JSON.stringify(version.config, null, 2),
      endpoints: JSON.stringify(version.endpoints, null, 2),
      changelog: version.changelog || '',
      is_available: version.is_available,
      is_default: version.is_default,
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.version) {
      toast({
        title: 'Versão é obrigatória',
        variant: 'destructive',
      });
      return;
    }

    let config, endpoints;
    try {
      config = JSON.parse(formData.config);
      endpoints = JSON.parse(formData.endpoints);
    } catch {
      toast({
        title: 'JSON inválido em config ou endpoints',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        provider_id: providerId,
        version: formData.version,
        status: formData.status,
        config,
        endpoints,
        changelog: formData.changelog || null,
        is_available: formData.is_available,
        is_default: formData.is_default,
        released_at: formData.status === 'stable' && formData.is_available ? new Date().toISOString() : null,
      };

      if (editingVersion) {
        const { error } = await supabase
          .from('provider_versions')
          .update(payload)
          .eq('id', editingVersion.id);

        if (error) throw error;
        toast({ title: 'Versão atualizada!' });
      } else {
        const { error } = await supabase
          .from('provider_versions')
          .insert(payload);

        if (error) throw error;
        toast({ title: 'Versão criada!' });
      }

      setEditDialogOpen(false);
      fetchVersions();
    } catch (error: any) {
      console.error('Error saving version:', error);
      toast({
        title: 'Erro ao salvar versão',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailable = async (version: ProviderVersion) => {
    try {
      const { error } = await supabase
        .from('provider_versions')
        .update({ is_available: !version.is_available })
        .eq('id', version.id);

      if (error) throw error;
      toast({
        title: version.is_available ? 'Versão ocultada' : 'Versão disponibilizada',
      });
      fetchVersions();
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast({
        title: 'Erro ao atualizar',
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (version: ProviderVersion) => {
    try {
      const { error } = await supabase
        .from('provider_versions')
        .update({ is_default: true })
        .eq('id', version.id);

      if (error) throw error;
      toast({ title: 'Versão definida como padrão' });
      fetchVersions();
    } catch (error) {
      console.error('Error setting default:', error);
      toast({
        title: 'Erro ao definir padrão',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Versões - {providerName}</DialogTitle>
            <DialogDescription>
              Gerencie as versões de integração deste fornecedor.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end mb-4">
            <Button onClick={handleOpenCreate} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Nova Versão
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Versão</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Disponível</TableHead>
                <TableHead>Padrão</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : versions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Nenhuma versão cadastrada.
                  </TableCell>
                </TableRow>
              ) : (
                versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell className="font-mono font-medium">
                      v{version.version}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusConfig[version.status].color}>
                        {statusConfig[version.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={version.is_available}
                        onCheckedChange={() => handleToggleAvailable(version)}
                      />
                    </TableCell>
                    <TableCell>
                      {version.is_default ? (
                        <Badge variant="secondary">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Padrão
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(version)}
                          disabled={!version.is_available}
                        >
                          Definir
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(version)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Version Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {editingVersion ? 'Editar Versão' : 'Nova Versão'}
              </DialogTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setHelpDialogOpen(true)}
                className="gap-2"
              >
                <HelpCircle className="h-4 w-4" />
                Ajuda
              </Button>
            </div>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="version">Versão *</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="Ex: 1.0.0"
              />
            </div>

            <div className="grid gap-2">
              <Label>Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: VersionStatus) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="config">Configuração (JSON)</Label>
              <Textarea
                id="config"
                value={formData.config}
                onChange={(e) => setFormData({ ...formData, config: e.target.value })}
                placeholder="{}"
                rows={4}
                className="font-mono text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="endpoints">Endpoints (JSON)</Label>
              <Textarea
                id="endpoints"
                value={formData.endpoints}
                onChange={(e) => setFormData({ ...formData, endpoints: e.target.value })}
                placeholder="{}"
                rows={4}
                className="font-mono text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="changelog">Changelog</Label>
              <Textarea
                id="changelog"
                value={formData.changelog}
                onChange={(e) => setFormData({ ...formData, changelog: e.target.value })}
                placeholder="Descreva as mudanças desta versão..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_available">Disponível para seleção</Label>
              <Switch
                id="is_available"
                checked={formData.is_available}
                onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_default">Versão padrão</Label>
              <Switch
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : editingVersion ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Guia de Configuração de Versões
            </DialogTitle>
            <DialogDescription>
              Entenda como preencher os campos JSON para configurar corretamente cada versão do provider.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Configuração Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  📌 Configuração (JSON)
                </h3>
                <p className="text-sm text-muted-foreground">
                  Define as configurações específicas desta versão do provider. Controla comportamento, limites e funcionalidades suportadas.
                </p>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Campos comuns:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li><code className="bg-muted px-1 rounded">api_version</code> - Versão da API do fornecedor</li>
                    <li><code className="bg-muted px-1 rounded">timeout</code> - Tempo limite para requisições (ms)</li>
                    <li><code className="bg-muted px-1 rounded">retry_attempts</code> - Tentativas em caso de falha</li>
                    <li><code className="bg-muted px-1 rounded">features</code> - Array de funcionalidades suportadas</li>
                    <li><code className="bg-muted px-1 rounded">rate_limit</code> - Limites de taxa de envio</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Exemplo para Z-API:</h4>
                  <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "api_version": "v2",
  "timeout": 30000,
  "retry_attempts": 3,
  "features": ["text", "image", "audio", "document", "location"],
  "rate_limit": {
    "messages_per_second": 10,
    "messages_per_day": 10000
  },
  "webhook_events": ["message", "status", "connection"]
}`}
                  </pre>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Exemplo para Evolution API:</h4>
                  <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "api_version": "1.6.0",
  "timeout": 60000,
  "retry_attempts": 5,
  "features": ["text", "image", "audio", "video", "document", "sticker"],
  "instance_mode": "single",
  "qrcode_timeout": 45,
  "auto_reconnect": true
}`}
                  </pre>
                </div>
              </div>

              <div className="border-t" />

              {/* Endpoints Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  📌 Endpoints (JSON)
                </h3>
                <p className="text-sm text-muted-foreground">
                  Define as URLs e métodos HTTP para cada operação da integração. O sistema usa essas configurações para fazer as chamadas corretas à API do provider.
                </p>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Campos comuns:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li><code className="bg-muted px-1 rounded">send_text</code> - Envio de mensagens de texto</li>
                    <li><code className="bg-muted px-1 rounded">send_media</code> - Envio de mídia (imagem, áudio, vídeo)</li>
                    <li><code className="bg-muted px-1 rounded">get_status</code> - Verificar status de conexão</li>
                    <li><code className="bg-muted px-1 rounded">get_qrcode</code> - Obter QR Code para conexão</li>
                    <li><code className="bg-muted px-1 rounded">disconnect</code> - Desconectar sessão</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Exemplo para Z-API:</h4>
                  <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "send_text": {
    "method": "POST",
    "path": "/send-text"
  },
  "send_image": {
    "method": "POST",
    "path": "/send-image"
  },
  "send_audio": {
    "method": "POST",
    "path": "/send-audio"
  },
  "get_status": {
    "method": "GET",
    "path": "/status"
  },
  "get_qrcode": {
    "method": "GET",
    "path": "/qr-code/image"
  },
  "disconnect": {
    "method": "POST",
    "path": "/disconnect"
  }
}`}
                  </pre>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Exemplo para Evolution API:</h4>
                  <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "send_text": {
    "method": "POST",
    "path": "/message/sendText/{instance}"
  },
  "send_media": {
    "method": "POST",
    "path": "/message/sendMedia/{instance}"
  },
  "get_status": {
    "method": "GET",
    "path": "/instance/connectionState/{instance}"
  },
  "get_qrcode": {
    "method": "GET",
    "path": "/instance/connect/{instance}"
  },
  "create_instance": {
    "method": "POST",
    "path": "/instance/create"
  },
  "delete_instance": {
    "method": "DELETE",
    "path": "/instance/delete/{instance}"
  }
}`}
                  </pre>
                </div>
              </div>

              <div className="border-t" />

              {/* Tips Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  💡 Dicas
                </h3>
                <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                  <li>Sempre valide o JSON antes de salvar (pode usar ferramentas online)</li>
                  <li>Use <code className="bg-muted px-1 rounded">{"{instance}"}</code> como placeholder para valores dinâmicos</li>
                  <li>Mantenha consistência nos nomes dos endpoints entre versões</li>
                  <li>Documente mudanças significativas no campo Changelog</li>
                  <li>Teste a versão em desenvolvimento antes de marcar como Estável</li>
                </ul>
              </div>
            </div>
          </ScrollArea>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setHelpDialogOpen(false)}>
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
